import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { cloneDeep, remove } from "lodash";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ITab } from "~app/common/components/tabs/tab/itab";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { INamedSokfilter, ISparadeSokfilter } from "../../common/models/jlp-model";

@Component({
    selector: "rask-jlp-saved-searches",
    templateUrl: "./jlp-saved-searches.component.html",
    styleUrls: ["./jlp-saved-searches.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonComponent,
        InputComponent,
        FormsModule,
        ButtonComponent,
        TabsComponent,
        TabComponent,
        TranslatePipe
    ]
})
export class JlpSavedSearchesComponent implements OnInit, OnChanges, OnDestroy {

    @Input() WIK: string;
    errors: string[];
    model: INamedSokfilter = { namn: "" };
    @Input() name: string;
    @Output() selectedFilter: EventEmitter<INamedSokfilter> = new EventEmitter<INamedSokfilter>();
    show = false;
    sokningar: INamedSokfilter[];
    @Output() triggerSave = new EventEmitter();
    @Input() triggerSparaFilter: any;

    private readonly $translate = inject(TranslateService);
    private readonly $unsubscribe: Subject<void> = new Subject();
    private readonly maxSavedFilters = 45;
    private readonly minaSparadeFilter$: BehaviorSubject<INamedSokfilter[]> = new BehaviorSubject<INamedSokfilter[]>([]);
    private readonly modalService = inject(RaskModalService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    get minaSparadeFilter(): Observable<INamedSokfilter[]> {
        return this.minaSparadeFilter$.asObservable();
    }

    abort() {
        this.close();
    }

    close() {
        this.errors = [];
        this.show = false;
        this.reset();
    }

    confirmDeleteSparadSokning(tab: ITab) {
        this.modalService.confirm(new ModalSettings(this.$translate.instant("JUSTERA_LEVERANSPLAN_TA_BORT_SOKNING"),
            `${this.$translate.instant("JUSTERA_LEVERANSPLAN_BEKRAFTA_BORTTAGNING_AV")} "${tab.label}"`))
            .pipe(take(1))
            .subscribe((ok) => {
                if (ok === ConfirmDialogResponse.save && this.sokningar.find(x => x.namn === tab.label)) {
                    this.deleteSearchFilterByNamn(tab.label);
                }
            });
    }

    getInstallningar() {
        this.webinstallningarService
            .getAsync(this.WIK)
            .then((value: ISparadeSokfilter) => {
                if (value && value.minaFilter) {
                    this.minaSparadeFilter$.next(value.minaFilter);
                }
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (ObjectUtils.hasValue(changes.triggerSparaFilter.currentValue)
            && changes.triggerSparaFilter.currentValue !== changes.triggerSparaFilter.previousValue) {
            this.model = Object.assign(this.model, changes.triggerSparaFilter.currentValue) as INamedSokfilter;
            this.model.namn = "";
            this.show = true;
        }
    }

    ngOnDestroy(): void {
        this.$unsubscribe.next();
        this.$unsubscribe.complete();
    }

    ngOnInit(): void {
        this.getInstallningar();

        this.minaSparadeFilter
            .pipe(takeUntil(this.$unsubscribe))
            .subscribe(x => {
                this.sokningar = Object.assign([], x);
            });
    }

    saveSearchParams() {
        this.triggerSave.emit();
    }

    setSokning(namedSokfilter: INamedSokfilter) {
        if (namedSokfilter) {
            const sokning = cloneDeep(namedSokfilter) as any;
            this.selectedFilter.emit(sokning);
        }
    }

    trySave() {
        if (!this.model.namn) {
            this.errors = [this.$translate.instant("JUSTERA_LEVERANSPLAN_ANGE_NAMN")];
            return;
        }
        if (!this.nameIsUnique(this.model)) {
            const ejUniktNamn = this.model.namn;
            this.modalService.confirm(new ModalSettings(this.$translate.instant("JUSTERA_LEVERANSPLAN_DET_FINNS_REDAN_SOKNING"),
                `${this.$translate.instant("JUSTERA_LEVERANSPLAN_VILL_DU_SKRIVA_OVER")}"${ejUniktNamn}"?`))
                .pipe(take(1))
                .subscribe((ok: string) => {
                    if (ok === ConfirmDialogResponse.cancel) {
                        return;
                    }
                    this.updateSearchFilterByNamn(this.model);
                    this.close();
                });
        } else {
            if (this.saveLimitReached()) {
                this.errors = [this.$translate.instant("JUSTERA_LEVERANSPLAN_GAR_EJ_ATT_SPARA_FLER_SOKNINGAR")];
                return;
            }
            this.saveSearchFilter(this.model);
            this.close();
        }
    }

    private deleteSearchFilterByNamn(namn: string) {
        remove(this.minaSparadeFilter$.value, f => this.isEqualIgnoreCaseAndWhitespace(f.namn, namn));

        this.webinstallningarService
            .setAsync(this.WIK, {
                minaFilter: this.minaSparadeFilter$.value
            } as any)
            .then((result) => {
                this.getInstallningar();
            });
    }

    private isEqualIgnoreCaseAndWhitespace(a: string, b: string): boolean {
        return a.trim().toLowerCase() === b.trim().toLowerCase();
    }

    private nameIsUnique(filter: INamedSokfilter) {
        return !this.minaSparadeFilter$.value
            || !this.minaSparadeFilter$.value.some(x => x.namn.toLowerCase().trim() === filter.namn.toLowerCase().trim());
    }

    private reset() {
        this.model = { namn: "" };
    }

    private saveLimitReached() {
        return this.minaSparadeFilter$.value && this.minaSparadeFilter$.value.length >= this.maxSavedFilters;
    }

    private saveSearchFilter(namedSearchFilter: INamedSokfilter) {
        this.webinstallningarService
            .setAsync(this.WIK, {
                minaFilter: [
                    ...this.minaSparadeFilter$.value,
                    ...[namedSearchFilter]
                ]
            })
            .then((result) => {
                this.getInstallningar();
            });
    }

    private updateSearchFilterByNamn(namedSearchFilter: INamedSokfilter) {
        remove(this.minaSparadeFilter$.value, f => this.isEqualIgnoreCaseAndWhitespace(f.namn, namedSearchFilter.namn));
        this.saveSearchFilter(namedSearchFilter);
    }
}
